import { PropsWithChildren } from 'react';

import { cn } from '@/shared/helpers';

import { ActionPanel } from './action-panel';
import { ConditionPanel } from './condition-panel';
import { WorkflowEditorState } from './use-workflow-editor-state';

/**
 * The editor's side slot: one persistent `<aside>` hosting either the
 * no-selection `children` or the selected node's panel. A single element so
 * the width animates between the two states instead of remounting.
 */
export function WorkflowEditorSide({
  editor,
  children,
}: PropsWithChildren<{ editor: WorkflowEditorState }>) {
  const expanded = !!editor.selection;
  return (
    <aside
      className={cn(
        'order-1 min-h-0 w-full min-w-0 shrink-0 transition-[width] @2xl:order-none @2xl:border-l',
        // Expanded: a wide flush slot the panel fills. Collapsed: the narrower
        // padded, self-scrolling sidebar, matching DetailSide.
        expanded
          ? '@2xl:w-120 @2xl:max-w-[50%] @2xl:overflow-hidden @5xl:w-176'
          : '@2xl:w-80 @2xl:overflow-x-hidden @2xl:overflow-y-auto @5xl:w-96',
      )}
    >
      {expanded ? (
        // Named container so the panels' innards can respond to the slot width.
        <div className="@container/side h-full">
          <WorkflowEditorPanel editor={editor} />
        </div>
      ) : (
        // `@container/side` sits on the inner element so the `@2xl:` padding
        // resolves against the page container, not the narrow aside.
        <div className="@container/side flex min-w-0 flex-col gap-5 *:shrink-0 @2xl:p-(--page-inset) @2xl:pl-5">
          {children}
        </div>
      )}
    </aside>
  );
}

/**
 * The panel body for the selected node. Each variant is keyed on its node
 * identity so per-node input state resets when the selection moves.
 */
export function WorkflowEditorPanel({ editor }: { editor: WorkflowEditorState }) {
  const { selection } = editor;
  if (!selection) {
    return null;
  }
  if (selection.kind === 'condition') {
    return (
      <ConditionPanel key={selection.ruleIndex} editor={editor} ruleIndex={selection.ruleIndex} />
    );
  }
  return (
    <ActionPanel
      key={`${selection.ruleIndex}-${selection.branch}-${selection.actionIndex}`}
      editor={editor}
      ruleIndex={selection.ruleIndex}
      branch={selection.branch}
      actionIndex={selection.actionIndex}
    />
  );
}
